import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Mail,
  Lock,
  CreditCard,
  Bell,
  Settings as SettingsIcon,
  Shield,
  Download,
  Trash2,
  AlertTriangle,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
  Building2,
  Users
} from 'lucide-react';
import { supabase } from '@/lib/supabase-client';
import { toast } from 'sonner';
import { useSubscription } from '@/hooks/useSubscription';
import { BillingHistoryModal } from '@/components/BillingHistoryModal';

export default function Settings() {
  const { user, isRecruiter, isRecruiterValue, organizationData } = useAuth();
  const navigate = useNavigate();
  const { subscription, isLoading: isSubscriptionLoading, isActive, product } = useSubscription();
  const [organizationMembers, setOrganizationMembers] = useState<Array<{ full_name: string }>>([]);

  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false);
  const [currentEmail, setCurrentEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);

  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const [isBillingHistoryOpen, setIsBillingHistoryOpen] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  useEffect(() => {
    const fetchOrganizationMembers = async () => {
      if (!isRecruiterValue || !organizationData) return;

      try {
        const { data, error } = await supabase
          .from('organization_members')
          .select('user_id, user_profiles(full_name)')
          .eq('organization_id', organizationData.id);

        if (error) {
          console.error('Error fetching organization members:', error);
          return;
        }

        const members = data?.map((member: any) => ({
          full_name: member.user_profiles?.full_name || 'Unknown'
        })) || [];

        setOrganizationMembers(members);
      } catch (error) {
        console.error('Error fetching organization members:', error);
      }
    };

    fetchOrganizationMembers();
  }, [isRecruiter, organizationData]);

  const handleOpenChangeEmail = () => {
    setCurrentEmail(user?.email || '');
    setNewEmail('');
    setIsChangeEmailModalOpen(true);
  };

  const handleChangeEmail = async () => {
    if (!currentEmail || !newEmail) {
      alert('Please fill in all fields');
      return;
    }

    if (currentEmail !== user?.email) {
      alert('Current email does not match your account email');
      return;
    }

    if (newEmail === currentEmail) {
      alert('New email must be different from current email');
      return;
    }

    setIsUpdatingEmail(true);

    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail });

      if (error) throw error;

      alert('Email update initiated! Please check both your old and new email addresses for confirmation links.');
      setIsChangeEmailModalOpen(false);
      setCurrentEmail('');
      setNewEmail('');
    } catch (error: any) {
      console.error('Error updating email:', error);
      alert(error.message || 'Failed to update email. Please try again.');
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      alert('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      alert('New password and confirmation do not match');
      return;
    }

    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    if (newPassword === currentPassword) {
      alert('New password must be different from current password');
      return;
    }

    setIsUpdatingPassword(true);

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: currentPassword,
      });

      if (signInError) {
        throw new Error('Current password is incorrect');
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) throw updateError;

      alert('Password updated successfully!');
      setIsChangePasswordModalOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Error updating password:', error);
      alert(error.message || 'Failed to update password. Please try again.');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleDownloadData = async () => {
    const exportPromise = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('You must be logged in to export your data');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-user-data`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to export data');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    };

    toast.promise(exportPromise(), {
      loading: 'Preparing your data export...',
      success: 'Your data has been exported successfully',
      error: (err) => err.message || 'Failed to export data',
    });
  };

  const handleOpenCustomerPortal = async () => {
    setIsOpeningPortal(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        toast.error('You must be logged in');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-customer-portal`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            return_url: `${window.location.origin}/`,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to open customer portal');
      }

      window.open(result.url, '_blank');
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to open billing portal');
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const handleDeleteAccount = () => {
    setDeleteConfirmText('');
    setIsDeleteAccountModalOpen(true);
  };

  const handleConfirmDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      return;
    }

    setIsDeletingAccount(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        toast.error('You must be logged in to delete your account');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user-account`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete account');
      }

      toast.success('Your account has been permanently deleted');
      setIsDeleteAccountModalOpen(false);
      setDeleteConfirmText('');

      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete account. Please try again.');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
          <p className="text-gray-600">Manage your account settings and preferences</p>
        </div>

        <Card className="shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-gray-700" />
              <CardTitle>My Account</CardTitle>
            </div>
            <CardDescription>Manage your account information and security</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={user?.email || ''}
                disabled
                className="bg-gray-50"
              />
            </div>

            <Separator />

            <div className="flex flex-wrap gap-3">
              <Button variant="outline" className="gap-2" onClick={handleOpenChangeEmail}>
                <Mail className="w-4 h-4" />
                Change Email
              </Button>
              <Button variant="outline" className="gap-2" onClick={() => setIsChangePasswordModalOpen(true)}>
                <Lock className="w-4 h-4" />
                Change Password
              </Button>
            </div>
          </CardContent>
        </Card>

        {isRecruiterValue && organizationData && (
          <Card className="shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-gray-700" />
                <CardTitle>Organization</CardTitle>
              </div>
              <CardDescription>Your organization information and team members</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Organization Name</Label>
                  <Input
                    value={organizationData.name}
                    disabled
                    className="bg-gray-50"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Email Domains</Label>
                  <div className="flex flex-wrap gap-2">
                    {organizationData.email_domains.map((domain, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                      >
                        @{domain}
                      </span>
                    ))}
                  </div>
                </div>

                {organizationData.description && (
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <p className="text-sm text-gray-700">{organizationData.description}</p>
                  </div>
                )}

                <Separator />

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-600" />
                    <Label>Team Members ({organizationMembers.length})</Label>
                  </div>
                  {organizationMembers.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {organizationMembers.map((member, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg"
                        >
                          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                            {member.full_name.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm text-gray-900">{member.full_name}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No other members in your organization</p>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  To update organization details, please contact your administrator.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-gray-700" />
              <CardTitle>Subscription & Billing</CardTitle>
            </div>
            <CardDescription>Manage your subscription and payment methods</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isSubscriptionLoading ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                <span className="text-sm text-gray-600">Loading subscription details...</span>
              </div>
            ) : isActive && subscription && product ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Current Plan</p>
                    <p className="text-sm text-gray-600 mt-1">{product.name}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {product.currencySymbol}{product.price.toFixed(2)}/{product.mode === 'subscription' ? 'month' : 'one-time'}
                      {subscription.current_period_end && (
                        <> · Renews {new Date(subscription.current_period_end * 1000).toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                      )}
                    </p>
                    {subscription.cancel_at_period_end && (
                      <p className="text-xs text-orange-600 mt-1 font-medium">
                        Cancels at end of billing period
                      </p>
                    )}
                    {subscription.payment_method_last4 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Payment: {subscription.payment_method_brand} ····{subscription.payment_method_last4}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  </div>
                </div>
              </div>
            ) : subscription?.subscription_status === 'past_due' ? (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Payment Issue</p>
                    <p className="text-sm text-orange-700 mt-1">
                      Your subscription payment failed. Please update your payment method.
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      Past Due
                    </span>
                  </div>
                </div>
              </div>
            ) : subscription?.subscription_status === 'canceled' ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Subscription Canceled</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Your subscription has been canceled.
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Canceled
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div>
                  <p className="font-semibold text-gray-900">Free Tier</p>
                  <p className="text-sm text-gray-600 mt-1">
                    You are currently on the free tier
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Upgrade to unlock more interview sessions and features
                  </p>
                </div>
              </div>
            )}

            <Separator />

            <div className="flex flex-wrap gap-3">
              {(subscription?.subscription_status === 'active' || subscription?.subscription_status === 'past_due') ? (
                <>
                  <Button
                    variant="outline"
                    onClick={handleOpenCustomerPortal}
                    disabled={isOpeningPortal}
                  >
                    {isOpeningPortal && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Manage Subscription
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setIsBillingHistoryOpen(true)}
                  >
                    Billing History
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleOpenCustomerPortal}
                    disabled={isOpeningPortal}
                  >
                    {isOpeningPortal && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Update Payment Method
                  </Button>
                </>
              ) : (
                <Button onClick={() => navigate('/pricing')}>
                  View Plans
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-gray-700" />
              <CardTitle>Contact & Support</CardTitle>
            </div>
            <CardDescription>Get help or send us your feedback</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">Support Email</Label>
              <a
                href="mailto:support@techinterviews.com"
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                support@techinterviews.com
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </CardContent>
        </Card>

        {/* TODO: NOTIFICATIONS FEATURE - NOT YET IMPLEMENTED
            To enable this feature, implement:
            1. Database table: notification_preferences (user_id, email_notifications, interview_reminders, performance_summaries)
            2. Email service integration (Resend, SendGrid, etc.)
            3. Edge functions for sending emails
            4. Scheduled jobs for reminders and summaries
        */}
        {/* <Card className="shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-gray-700" />
              <CardTitle>Notifications</CardTitle>
            </div>
            <CardDescription>Choose what notifications you want to receive</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="email-notifications" className="font-medium">Email Notifications</Label>
                <p className="text-sm text-gray-500">Receive email updates about your account</p>
              </div>
              <Switch
                id="email-notifications"
                checked={emailNotifications}
                onCheckedChange={setEmailNotifications}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="interview-reminders" className="font-medium">Interview Reminders</Label>
                <p className="text-sm text-gray-500">Get reminded about upcoming practice sessions</p>
              </div>
              <Switch
                id="interview-reminders"
                checked={interviewReminders}
                onCheckedChange={setInterviewReminders}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="performance-summaries" className="font-medium">Performance Summaries</Label>
                <p className="text-sm text-gray-500">Weekly performance and progress reports</p>
              </div>
              <Switch
                id="performance-summaries"
                checked={performanceSummaries}
                onCheckedChange={setPerformanceSummaries}
              />
            </div>
          </CardContent>
        </Card> */}

        {/* TODO: APP PREFERENCES FEATURE - NOT YET IMPLEMENTED
            To enable this feature, implement:
            1. Database table: user_preferences (user_id, default_mode, feedback_level, default_difficulty)
            2. Load/save preferences from database
            3. Apply preferences when starting interviews
            4. Integration with interview config system
        */}
        {/* <Card className="shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-gray-700" />
              <CardTitle>App Preferences</CardTitle>
            </div>
            <CardDescription>Customize your interview experience</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="default-mode">Default Interview Mode</Label>
              <Select defaultValue="standard">
                <SelectTrigger id="default-mode">
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard Interview</SelectItem>
                  <SelectItem value="behavioral">Behavioral Focus</SelectItem>
                  <SelectItem value="technical">Technical Focus</SelectItem>
                  <SelectItem value="custom">Custom Mode</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-level">Feedback Detail Level</Label>
              <Select defaultValue="detailed">
                <SelectTrigger id="feedback-level">
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="brief">Brief</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="detailed">Detailed</SelectItem>
                  <SelectItem value="comprehensive">Comprehensive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="difficulty">Default Difficulty</Label>
              <Select defaultValue="medium">
                <SelectTrigger id="difficulty">
                  <SelectValue placeholder="Select difficulty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card> */}

        <Card className="shadow-sm border-red-200">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-gray-700" />
              <CardTitle>Privacy & Data</CardTitle>
            </div>
            <CardDescription>Manage your data and privacy settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-3">
                  Download a copy of all your interview data, performance metrics, and account information
                </p>
                <Button variant="outline" onClick={handleDownloadData} className="gap-2">
                  <Download className="w-4 h-4" />
                  Download My Data
                </Button>
              </div>

              <Separator />

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-red-900 mb-1">Delete Account</h4>
                    <p className="text-sm text-red-700 mb-4">
                      Permanently delete your account and all associated data. This action cannot be undone.
                    </p>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteAccount}
                      className="gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete My Account
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle>About</CardTitle>
            <CardDescription>Application information and legal</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Version</span>
              <span className="font-medium text-gray-900">1.0.0</span>
            </div>

            <Separator />

            <div className="flex flex-wrap gap-4 text-sm">
              <a href="#" className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                Terms of Service
                <ExternalLink className="w-3 h-3" />
              </a>
              <a href="#" className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                Privacy Policy
                <ExternalLink className="w-3 h-3" />
              </a>
              <a href="#" className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                Cookie Policy
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            <Separator />

            <p className="text-xs text-gray-500">
              © 2024 teech Interviews. All rights reserved.
            </p>
          </CardContent>
        </Card>

        <Dialog open={isChangeEmailModalOpen} onOpenChange={setIsChangeEmailModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Change Email Address
              </DialogTitle>
              <DialogDescription>
                Update your account email address. You'll need to verify the new email.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="current-email">Current Email</Label>
                <Input
                  id="current-email"
                  type="email"
                  placeholder="current@example.com"
                  value={currentEmail}
                  onChange={(e) => setCurrentEmail(e.target.value)}
                  disabled={isUpdatingEmail}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-email">New Email</Label>
                <Input
                  id="new-email"
                  type="email"
                  placeholder="new@example.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  disabled={isUpdatingEmail}
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setIsChangeEmailModalOpen(false)}
                disabled={isUpdatingEmail}
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangeEmail}
                disabled={isUpdatingEmail || !currentEmail || !newEmail}
              >
                {isUpdatingEmail ? 'Updating...' : 'Update Email'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isChangePasswordModalOpen} onOpenChange={setIsChangePasswordModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Change Password
              </DialogTitle>
              <DialogDescription>
                Enter your current password and choose a new one.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Current Password</Label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    placeholder="Enter current password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    disabled={isUpdatingPassword}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isUpdatingPassword}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500">Must be at least 6 characters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isUpdatingPassword}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsChangePasswordModalOpen(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                disabled={isUpdatingPassword}
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangePassword}
                disabled={isUpdatingPassword || !currentPassword || !newPassword || !confirmPassword}
              >
                {isUpdatingPassword ? 'Updating...' : 'Update Password'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isDeleteAccountModalOpen} onOpenChange={setIsDeleteAccountModalOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-6 h-6" />
                Delete Account
              </DialogTitle>
              <DialogDescription className="text-base">
                This action cannot be undone. Please read carefully before proceeding.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                <p className="font-semibold text-red-900">
                  The following data will be permanently deleted:
                </p>
                <ul className="text-sm text-red-800 space-y-2 ml-4 list-disc">
                  <li>All interview sessions and recordings</li>
                  <li>Performance metrics and analytics</li>
                  <li>Job postings and applications</li>
                  <li>Profile information and preferences</li>
                  <li>Resume and uploaded documents</li>
                  <li>All account settings</li>
                </ul>
                <p className="text-sm text-red-900 font-semibold mt-3">
                  This action is irreversible and cannot be recovered.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="delete-confirm" className="text-gray-900">
                  Type <span className="font-mono font-bold">DELETE</span> to confirm
                </Label>
                <Input
                  id="delete-confirm"
                  type="text"
                  placeholder="Type DELETE here"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  disabled={isDeletingAccount}
                  className="font-mono"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteAccountModalOpen(false);
                  setDeleteConfirmText('');
                }}
                disabled={isDeletingAccount}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDeleteAccount}
                disabled={isDeletingAccount || deleteConfirmText !== 'DELETE'}
                className="gap-2"
              >
                {isDeletingAccount ? (
                  <>Deleting Account...</>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete My Account
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <BillingHistoryModal
          open={isBillingHistoryOpen}
          onOpenChange={setIsBillingHistoryOpen}
        />
      </div>
    </div>
  );
}
